import { startOfDay, subDays, subMonths, subYears } from "date-fns";
import type { DateRange } from "./date-utils";

/**
 * A quick-select option for the date range picker — mirrors Material UI's
 * range-picker "shortcuts" slot.
 */
export interface DateRangePreset {
  readonly id: string;
  readonly label: string;
  /** Computes the concrete range for a given "today" reference date. */
  readonly getRange: (today: Date) => DateRange;
}

/** Builds a range of `days` ending on (and including) `today`. */
function lastNDays(days: number): (today: Date) => DateRange {
  return (today: Date) => ({
    start: startOfDay(subDays(today, days - 1)),
    end: startOfDay(today),
  });
}

/**
 * Default range shortcuts: 1 day, 1 week, 1 fortnight, 1 month, 3 months —
 * each ending today. Consumers can override via the `presets` prop.
 */
export const defaultRangePresets: readonly DateRangePreset[] = [
  { id: "1d", label: "Today", getRange: lastNDays(1) },
  { id: "1w", label: "Last 7 days", getRange: lastNDays(7) },
  { id: "2w", label: "Last 14 days", getRange: lastNDays(14) },
  {
    id: "1m",
    label: "Last month",
    getRange: (today) => ({ start: startOfDay(subMonths(today, 1)), end: startOfDay(today) }),
  },
  {
    id: "6m",
    label: "Last 6 months",
    getRange: (today) => ({ start: startOfDay(subMonths(today, 6)), end: startOfDay(today) }),
  },
  {
    id: "1y",
    label: "Last year",
    getRange: (today) => ({ start: startOfDay(subYears(today, 1)), end: startOfDay(today) }),
  },
];
